import {createContext, useContext, useEffect, useRef, useState} from 'react';
import {useQueryClient} from 'react-query';
import useLocalStorage from 'hooks/use-local-storage';
import {api, client} from 'utils/api-client';
import {toast} from 'react-toastify';
import { AxiosRequestConfig } from 'axios'

export interface Token {
  acsTokenExpired: number;
  refreshTokenExpired: number;
  companyId?: string;
  acsToken: string;
  refreshToken: string;
}

interface LoginParams {
  email?: string;
  password?: string;
  newPassword?: string;
  code?: string;
}

type SetPasswordRequest = {
  newPassword?: string;
  code?: string;
};

const TOKEN_LIFE = 1000 * 60 * 10; // 10min
const REFRESH_LIFE = 1000 * 60 * 60 * 24 * 10; // 10day

export interface IAuthContext {
  login: (loginParams: LoginParams) => Promise<any>;
  setPassword: (request: SetPasswordRequest) => void;
  checkPasswordEnable: (code: string) => Promise<boolean>;
  logout: () => void;
  getToken: (force?: boolean) => Promise<string | null>;
  clearLocalStorage: () => void;
  forceLogoutThenLogin: (request: LoginParams) => void;
  token?: Token | null;
  companyId: string;
  error?: any;
  isFetching: boolean;
  isLogin: boolean;
}

const AuthContext = createContext<IAuthContext>({
  login: () => Promise.resolve(),
  setPassword: () => {},
  checkPasswordEnable: () => Promise.resolve(true),
  logout: () => {},
  clearLocalStorage: () => {},
  getToken: async () => null,
  forceLogoutThenLogin: () => {},
  companyId: '',
  isFetching: false,
  isLogin: false,
});

export const useAuthContext = () => {
  return useContext(AuthContext);
};

const queryKey = 'token';
const tokenExpired = (token: Token | null) => {
  const now = new Date().getTime();

  return token?.acsTokenExpired ? token.acsTokenExpired < now : true;
};

const refreshExpired = (token: Token) => {
  const now = new Date().getTime();

  return token.refreshTokenExpired < now;
};

const checkPasswordEnable = async (code: string) => {
  try {
    const response = await api.get(
      `auth/code/exist?code=${encodeURIComponent(code || '')}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const sendPasswordEnable = async ({newPassword, code}: LoginParams) => {
  const now = new Date().getTime();
  let response = null;
  try {
    response = await api.post(
      `user/password?code=${encodeURIComponent(code || '')}`,
      {data: {newPassword}},
    );
    return {
      acsTokenExpired: now + TOKEN_LIFE,
      refreshTokenExpired: now + REFRESH_LIFE,
      companyId: response?.companyId,
      acsToken: response?.acsToken,
      refreshToken: response?.refreshToken,
    };
  } catch (error) {
    throw error;
  }
};
const sendLogin = async (data: LoginParams) => {
  const now = new Date().getTime();
  let response = null;
  try {
    response = await api.post(`auth/session`, {data: {logIn: data}});
    return {
      acsTokenExpired: now + TOKEN_LIFE,
      refreshTokenExpired: now + REFRESH_LIFE,
      companyId: response?.companyId,
      acsToken: response?.acsToken,
      refreshToken: response?.refreshToken,
    };
  } catch (error) {
    throw error;
  }
};

const sendLogout = async (
  getToken: (force?: boolean) => Promise<string | null>,
) => {
  let response = null;
  try {
    response = await api.delete(`auth/session`, {
      data: {},
      token: await getToken(true),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const sendRefresh = async (data: Token) => {
  const now = new Date().getTime();
  let response = null;
  try {
    response = await api.post(`auth/accesstoken`, {
      data: {},
      headers: {
        authorization: `Bearer ${data.refreshToken}`,
      },
    });
    return {
      acsTokenExpired: now + TOKEN_LIFE,
      refreshTokenExpired: data.refreshTokenExpired,
      acsToken: response?.acsToken,
      refreshToken: data.refreshToken,
      companyId: data.companyId,
    };
  } catch (error) {
    throw error;
  }
};

export const AuthProvider: React.FC = ({children}) => {
  const [token, setToken] = useLocalStorage<Token | null>(queryKey, null);
  const [companyId, setCompanyId] = useLocalStorage<any>('companyId', '', {serializer: str => str, parser: str => str});
  const [startBackgroundRefresh, setStartBackgroundRefresh] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [error, setError] = useState<any>(null);
  const refreshRef = useRef<() => Promise<Token | undefined>>();
  const refreshHandlerRef = useRef<any>();
  const refreshInterval = TOKEN_LIFE;
  const quertClient = useQueryClient();
  const refresh = async () => {
    setError(null);
    let newToken = undefined;

    try {
      newToken = await sendRefresh(token as Token);
    } catch (refreshError) {
      setToken(null);
      setError(refreshError);
    }
    // If token is undefined then refresh has failed
    if (newToken !== undefined) {
      setToken(newToken);
    }

    return newToken;
  };

  useEffect(() => {
    if (error) {
      toast.error(error.message, {
        position: 'top-center',
        hideProgressBar: true,
        draggable: false,
        autoClose: false,
        onClose: () => {
          setError(null);
        },
      });
    } else {
      toast.dismiss();
    }
  }, [error]);

  useEffect(() => {
    refreshRef.current = refresh;
  });

  useEffect(() => {
    if (startBackgroundRefresh) {
      const callback = () => {
        refreshRef.current && refreshRef.current();
      };

      refreshHandlerRef.current = setInterval(callback, refreshInterval);
    } else {
      clearInterval(refreshHandlerRef.current);
    }
    return () => clearInterval(refreshHandlerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startBackgroundRefresh]);

  useEffect(() => {
    if (!token) {
      clearLocalStorage();
      setIsLogin(false);
      setStartBackgroundRefresh(false);
    } else {
      setIsLogin(true);
      setStartBackgroundRefresh(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setPassword = async (request: SetPasswordRequest) => {
    setIsFetching(true);
    setError(null);

    try {
      const token = await sendPasswordEnable(request);
      setToken(token as Token);
      setCompanyId(token.companyId);
    } catch (loginError) {
      setError(loginError);
    }

    setIsFetching(false);
  };
  const login = async (loginParams: LoginParams) => {
    setIsFetching(true);
    setError(null);

    try {
      const token = await sendLogin(loginParams);
      setToken(token as Token);
      setCompanyId(token.companyId);
    } catch (loginError) {
      const isLogin = await api
        .get(`auth/session/exist?email=${loginParams.email}`)
        .then(resp => resp.isLogIn);

      if (isLogin) {
        setIsFetching(false);
        return Promise.reject();
      } else {
        setError(loginError);
      }
    }

    setIsFetching(false);
    return Promise.resolve();
  };

  const logout = async () => {
    setIsFetching(true);
    setError(null);

    try {
      await sendLogout(getToken);
      setToken(null);
    } catch (logoutError) {
      setError(logoutError);
    }
    setIsFetching(false);
  };

  async function forceLogoutThenLogin(request: LoginParams) {
    setIsFetching(true);
    setError(null);
    try {
      await api.delete(`auth/session/exist?email=${request.email}`);
      await login(request);
    } catch (error) {
      setError(error);
    }
    setIsFetching(false);
  }

  const getToken = async (force = false) => {
    if (!token) return null;

    if (refreshExpired(token)) {
      toast.error('連線階段已過期，請再次登入', {
        position: 'top-center',
        hideProgressBar: true,
        draggable: false,
        toastId: 'refreshToken_timeOut',
        autoClose: false,
      });
      setToken(null);
      return null;
    }

    if (tokenExpired(token) || force) {
      const newToken = await refresh();

      return newToken?.acsToken;
    }

    return token?.acsToken;
  };

  const clearLocalStorage = () => {
    localStorage.clear();
    quertClient.removeQueries(['company']);
    setToken(null);
    setCompanyId('');
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        setPassword,
        checkPasswordEnable,
        logout,
        getToken,
        clearLocalStorage,
        forceLogoutThenLogin,
        token,
        companyId,
        error,
        isFetching,
        isLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

type ClientOption = {
  data?: any;
  token?: any;
  hiddenErrorMessage?: boolean;
} & AxiosRequestConfig;

export type APIClient = {
  get: <T>(endpoint: string, config?: ClientOption) => Promise<T>;
  post: <T>(endpoint: string, config?: ClientOption) => Promise<T>;
  put: <T>(endpoint: string, config?: ClientOption) => Promise<T>;
  delete: <T>(endpoint: string, config?: ClientOption) => Promise<T>;
};
export const useClient = () => {
  const {getToken, token, clearLocalStorage} = useAuthContext();
  const baseRef = useRef(
    async (endpoint: string, config: ClientOption = {}, newToken?: string) => {
      try {
        const token = await getToken();
        const response = await client(endpoint, {...config, token});
        return Promise.resolve(response);
      } catch (error: any) {
        if (!config.hiddenErrorMessage) {
          switch (error?.code) {
            case 400:
              const errorMessage = error?.data && typeof error?.data === 'string' ? error.data.split('\r\n').join('、') : error.message;
              toast.error(errorMessage, {
                position: 'top-center',
                hideProgressBar: true,
                draggable: false,
                autoClose: false
              });
              break;
            case 401:
              await getToken(true);
              break;
            case 500:
              toast.error('系統異常', {
                position: 'top-center',
                hideProgressBar: true,
                draggable: false,
                autoClose: false
              });
              break;
            default:
              break;
          }
        }
      }
    },
  );

  useEffect(() => {
    baseRef.current = async (
      endpoint: string,
      config: ClientOption = {},
      retryToken?: string,
    ) => {
      if (!token) return;
      try {
        const _token = retryToken || (await getToken());
        const response = await client(endpoint, {...config, token: _token});
        return Promise.resolve(response);
      } catch (error: any) {
        if (!config.hiddenErrorMessage) {
          switch (error?.code) {
            case 400:
              const errorMessage = error?.data && typeof error?.data === 'string' ? error.data.split('\r\n').join('、') : error.message;
              // 後端仍會儲存資料，只是噴Exception 防止步驟狀態改變，故不顯示錯誤訊息
              // 使用return仍會繼續promise chain，不會進error
              if (errorMessage === 'PY模組已關閉' || errorMessage === 'PY模組已打開') return;
              toast.error(errorMessage, {
                position: 'top-center',
                hideProgressBar: true,
                draggable: false,
                autoClose: false
              });
              break;
            case 401:
              if (retryToken) {
                clearLocalStorage();
                return;
              }
              const newToken = (await getToken(true)) || '';
              return baseRef.current(endpoint, config, newToken);
            case 500:
              toast.error('系統異常', {
                position: 'top-center',
                hideProgressBar: true,
                draggable: false,
                autoClose: false
              });
              break;
            default:
              if (error.message) {
                toast.error(error.message, {
                  position: 'top-center',
                  hideProgressBar: true,
                  draggable: false,
                  autoClose: false
                });
              }
              break;
          }
        }
        return Promise.reject(error);
      }
    };
  }, [token]);
  return {
    get: (endpoint: string, config: ClientOption = {}) =>
      baseRef.current(endpoint, {method: 'GET', ...config}),
    post: (endpoint: string, config: ClientOption = {}) =>
      baseRef.current(endpoint, {method: 'POST', ...config}),
    put: (endpoint: string, config: ClientOption = {}) =>
      baseRef.current(endpoint, {method: 'PUT', ...config}),
    delete: (endpoint: string, config: ClientOption = {}) =>
      baseRef.current(endpoint, {method: 'DELETE', ...config}),
  };
};
